import { useCallback, useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import toast from "react-hot-toast";
import EventTable from "../../components/manage-events/event-table";
import { BASE_URL, API_ENDPOINTS } from "../../config/api.config";
import DeleteEventModal from "../../components/manage-events/delete-event";
import DuplicateEventModal from "../../components/manage-events/duplicate";

const ROLE_LABELS = {
  TRANSPORTATION: "Transportation",
  FOOD_SUPPLIES: "Food Supplies",
};

function ManageEvents() {
  const location = useLocation();
  const navigate = useNavigate();
  const [events, setEvents] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [isDuplicateOpen, setIsDuplicateOpen] = useState(false);
  const [isDeleteOpen, setIsDeleteOpen] = useState(false);
  const isMountedRef = useRef(true);

  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  const normalizeEvents = useCallback(
    (items) =>
      items.map((event) => ({
        ...event,
        attachments: Array.isArray(event.attachments)
          ? event.attachments.map((attachment, index) => {
              if (typeof attachment === "string") {
                return {
                  id: `${event.id}-attachment-${index}`,
                  name: attachment,
                };
              }

              if (attachment && typeof attachment === "object") {
                return {
                  ...attachment,
                  id: attachment.id ?? `${event.id}-attachment-${index}`,
                  name:
                    attachment.name ??
                    attachment.title ??
                    `Attachment ${index + 1}`,
                };
              }

              return {
                id: `${event.id}-attachment-${index}`,
                name: `Attachment ${index + 1}`,
              };
            })
          : [],
        roles: Array.isArray(event.roles)
          ? event.roles.map((role) => ({
              ...role,
              label: ROLE_LABELS[role.roleName] || role.label || role.roleName,
            }))
          : [],
      })),
    []
  );

  const loadEvents = useCallback(async () => {
    const token = localStorage.getItem("accessToken");
    if (!token) {
      const message = "Authentication required. Please sign in again.";
      if (isMountedRef.current) {
        setError(message);
        setEvents([]);
        setIsLoading(false);
      }
      toast.error(message);
      return;
    }

    if (isMountedRef.current) {
      setIsLoading(true);
      setError(null);
    }

    try {
      const response = await fetch(
        `${BASE_URL}${API_ENDPOINTS.NONPROFIT_EVENTS_LIST}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      const result = await response.json().catch(() => null);
      const statusCode = result?.statusCode ?? response.status;

      if (!response.ok || statusCode >= 400) {
        const message = result?.message || "Unable to fetch events.";
        throw new Error(message);
      }

      const payload = Array.isArray(result?.data) ? result.data : [];
      const normalized = normalizeEvents(payload);

      if (isMountedRef.current) {
        setEvents(normalized);
      }
    } catch (err) {
      if (isMountedRef.current) {
        const message = err?.message || "Unable to fetch events.";
        setError(message);
        setEvents([]);
      }
      toast.error(err?.message || "Unable to fetch events.");
    } finally {
      if (isMountedRef.current) {
        setIsLoading(false);
      }
    }
  }, [normalizeEvents]);

  useEffect(() => {
    loadEvents();

    if (location.state?.refresh) {
      navigate(location.pathname, { replace: true });
    }
  }, [loadEvents, location.pathname, location.state?.refresh, navigate]);

  const closeModals = () => {
    setSelectedEvent(null);
    setIsDuplicateOpen(false);
    setIsDeleteOpen(false);
  };

  const handleAction = useCallback((action, event) => {
    if (!action || !event) return;

    switch (action) {
      case "duplicate":
        setSelectedEvent(event);
        setIsDuplicateOpen(true);
        break;
      case "delete":
        setSelectedEvent(event);
        setIsDeleteOpen(true);
        break;
      default:
        break;
    }
  }, []);

  return (
    <div>
      <EventTable
        data={events}
        onAction={handleAction}
        isLoading={isLoading}
        errorMessage={error}
      />
      {isDuplicateOpen && selectedEvent && (
        <DuplicateEventModal
          event={selectedEvent}
          onClose={closeModals}
          onSuccess={() => {
            closeModals();
            loadEvents();
          }}
        />
      )}
      {isDeleteOpen && selectedEvent && (
        <DeleteEventModal
          event={selectedEvent}
          onClose={closeModals}
          onSuccess={() => {
            closeModals();
            loadEvents();
          }}
        />
      )}
    </div>
  );
}

export default ManageEvents;
